/**
 * UTC instants at millisecond precision.
 *
 * Every timestamp crossing a boundary is normalized before it is compared or
 * hashed, so an offset-bearing source timestamp and the same instant written in
 * Z form cannot produce two different windows or two different digests.
 */

export type Utc = string;

const UTC_MS = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

export class TimestampFormatError extends Error {
  constructor(value: string, reason: string) {
    super(`Invalid timestamp ${JSON.stringify(value)}: ${reason}`);
    this.name = "TimestampFormatError";
  }
}

/**
 * Whether a canonical-looking string is also a real instant.
 *
 * The shape alone proves nothing: `2026-99-99T99:99:99.999Z` has every digit in
 * the right place and names no moment in time. Parsing and writing the result
 * back is what separates the two — a real instant is its own round trip.
 */
function isRealInstant(canonical: string): boolean {
  const parsed = Date.parse(canonical);
  if (Number.isNaN(parsed)) return false;
  return new Date(parsed).toISOString() === canonical;
}

/** Accept any ISO-8601 instant (offsets included) and normalize to UTC millis. */
export function normalizeUtc(value: string | number | Date): Utc {
  if (value instanceof Date) return fromEpochMs(value.getTime());
  if (typeof value === "number") return fromEpochMs(value);
  if (typeof value !== "string") throw new TimestampFormatError(String(value), "not a string");
  const trimmed = value.trim();
  if (UTC_MS.test(trimmed)) {
    if (!isRealInstant(trimmed)) throw new TimestampFormatError(value, "not a valid instant");
    return trimmed;
  }
  if (!/^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/.test(trimmed)) {
    throw new TimestampFormatError(value, "not an ISO-8601 date or instant");
  }
  const candidate = trimmed.length === 10 ? `${trimmed}T00:00:00.000Z` : trimmed.replace(" ", "T");
  const parsed = Date.parse(/(?:Z|[+-]\d{2}:?\d{2})$/.test(candidate) ? candidate : `${candidate}Z`);
  if (Number.isNaN(parsed)) throw new TimestampFormatError(value, "not a valid instant");
  return fromEpochMs(parsed);
}

export function isUtc(value: unknown): value is Utc {
  return typeof value === "string" && UTC_MS.test(value) && isRealInstant(value);
}

export function fromEpochMs(epochMs: number): Utc {
  if (!Number.isFinite(epochMs)) throw new TimestampFormatError(String(epochMs), "not finite");
  return new Date(epochMs).toISOString();
}

export function toEpochMs(value: Utc): number {
  return Date.parse(value);
}

export function compareUtc(a: Utc, b: Utc): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function addMs(value: Utc, deltaMs: number): Utc {
  return fromEpochMs(toEpochMs(value) + deltaMs);
}

/** Half-open [start, end) membership; a null end means the window is open. */
export function withinInterval(instant: Utc, start: Utc, end: Utc | null): boolean {
  return instant >= start && (end === null || instant < end);
}

/** Half-open interval overlap, used for range queries over windows. */
export function intervalsOverlap(
  aStart: Utc,
  aEnd: Utc | null,
  bStart: Utc,
  bEnd: Utc | null,
): boolean {
  if (aEnd !== null && aEnd <= bStart) return false;
  if (bEnd !== null && bEnd <= aStart) return false;
  return true;
}

export const DAY_MS = 86_400_000;
